import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ChartTheme from "../classes/ChartTheme";
import ListWrapper from "../utils/ListWrapper";
import NumberFormatter from "../classes/NumberFormatter";
import CanvasView from "./CanvasView";
import ChartThemeData from "../classes/ChartThemeData";
import ChartNumberFormat from "../classes/ChartNumberFormat";
import ChartData from "../classes/ChartData";
import { CanvasViewController } from "./CanvasView";

export interface Bar2DChartProps extends BaseUIProps {
  key?: string;
  data: Array<ChartData>;
  padding?: ui.EdgeInsets;
  numberFormat?: ChartNumberFormat;
  setAdaptiveYMin?: boolean;
  theme?: ChartTheme;
  labelPadding?: ui.EdgeInsets;
  canvasPadding?: ui.EdgeInsets;
  _dataHash?: number;
}

class _Bar2DChartState extends ObservableComponent<Bar2DChartProps> {
  static defaultProps = {
    padding: null,
    numberFormat: null,
    setAdaptiveYMin: false,
    theme: ChartTheme.Fusion,
    labelPadding: null,
    canvasPadding: null,
    data: [],
  };
  canvasViewController: CanvasViewController = new CanvasViewController();
  public constructor(props: Bar2DChartProps) {
    super(props);

    this.initState();
  }
  public get data(): Array<ChartData> {
    return this.props.data;
  }
  public get padding(): ui.EdgeInsets {
    return this.props.padding;
  }
  public get numberFormat(): ChartNumberFormat {
    return this.props.numberFormat;
  }
  public get setAdaptiveYMin(): boolean {
    return this.props.setAdaptiveYMin;
  }
  public get theme(): ChartTheme {
    return this.props.theme;
  }
  public get labelPadding(): ui.EdgeInsets {
    return this.props.labelPadding;
  }
  public get canvasPadding(): ui.EdgeInsets {
    return this.props.canvasPadding;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.subscribeToList(this.data, "data");

    this.updateSyncCollProperty("data", this.props.data);

    this.updateSyncProperty("numberFormat", this.props.numberFormat);

    this.on(["canvasPadding"], this.rebuild);
  }
  public componentDidUpdate(prevProps: Bar2DChartProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.data !== this.props.data) {
      this.updateObservableColl("data", prevProps.data, this.props.data);

      this.fire("data", this);
    }

    if (prevProps.padding !== this.props.padding) {
      this.fire("padding", this);
    }

    if (prevProps.numberFormat !== this.props.numberFormat) {
      this.updateObservable(
        "numberFormat",
        prevProps.numberFormat,
        this.props.numberFormat
      );

      this.fire("numberFormat", this);
    }

    if (prevProps.setAdaptiveYMin !== this.props.setAdaptiveYMin) {
      this.fire("setAdaptiveYMin", this);
    }

    if (prevProps.theme !== this.props.theme) {
      this.fire("theme", this);
    }

    if (prevProps.labelPadding !== this.props.labelPadding) {
      this.fire("labelPadding", this);
    }

    if (prevProps.canvasPadding !== this.props.canvasPadding) {
      this.fire("canvasPadding", this);
    }
  }
  public render(): ReactNode {
    return ui.Container({
      padding: this.canvasPadding,
      child: CanvasView({
        controller: this.canvasViewController,
        onRepaint: () => {
          this.onRepaint();
        },
        d3eRef: ui.LayoutAware((bounds, globalPos) => {
          this.onLayoutChanged(bounds, globalPos);
        }, null),
      }),
      className: ui.join(this.props.className, "Bar2DChart x171 hc h v"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onRepaint = (): void => {
    this.doRepaint();
  };
  public doRepaint = (): void => {
    if (this.data.isEmpty) {
      return;
    }

    let themeData: ChartThemeData = ChartThemeData.getData(this.theme);

    let numFormat: ChartNumberFormat =
      this.numberFormat ?? new ChartNumberFormat();

    let canvas: ui.Canvas = this.canvasViewController.canvas;

    let size: ui.Size = this.canvasViewController.size;

    let defaultTextStyle: ui.TextStyle = this.canvasViewController.textStyle;

    let tempHeight: number = size.height;

    let minValue: number = this.data
      .map((d) => d.doubleValue)
      .fold(Number.infinity, (t, v) => Math.min(t, v));

    let maxValue: number = this.data
      .map((d) => d.doubleValue)
      .fold(-1 * Number.infinity, (t, v) => Math.max(t, v));

    //  Calculate Y-Axis Step Size so that we get 4 to 7 steps

    let startValue: number = minValue;

    if (minValue > 0.0) {
      if (!this.setAdaptiveYMin) {
        startValue = 0.0;
      }
    }

    let valueRange: number = maxValue - startValue;

    //  Check various step sizes until we endup 4 to 7 steps

    let step: number = 1.0e-5;

    while (true) {
      if (valueRange / (step * 2.0) < 7.0) {
        step = step * 2.0;

        break;
      }

      if (valueRange / (step * 5.0) < 7.0) {
        step = step * 5.0;

        break;
      }

      step = step * 10.0;

      if (valueRange / step < 7.0) {
        break;
      }
    }

    let steps: number = (valueRange / step).toInt();

    let minRange: number = 0.0;

    if (minValue > 0.0 && this.setAdaptiveYMin) {
      //  Start from the max and come down

      minRange = steps * step;

      while (this.data.any((d) => d.doubleValue < minRange)) {
        minRange -= step;
      }
    } else {
      while (this.data.any((d) => d.doubleValue < minRange)) {
        minRange -= step;
      }
    }

    let maxRange: number = 0.0;

    while (this.data.any((d) => d.doubleValue > maxRange)) {
      maxRange += step;
    }

    let totalRange: number = maxRange - minRange;

    //  Draw step labels

    let yAxisValuePainters: Array<ui.TextPainter> = [];

    let maxYAxisValueWidth: number = 0.0;

    let maxYAxisValueHeight: number = 0.0;

    //  Layout the X-Axis Labels

    let stepValue: number = minRange;

    for (let i: number = 0; i < steps + 2; i++) {
      // TextStyle textStyle = TextStyleExt.merge(defaultTextStyle, TextStyle(color: themeData.labelColor));

      let label: string = NumberFormatter.formatDouble(stepValue, numFormat);

      let style: ui.TextStyle = new ui.TextStyle({
        color: themeData.labelColor,
      });

      let textSpan: ui.TextSpan = new ui.TextSpan({
        text: label,
        style: style,
      });

      let textPainter: ui.TextPainter = new ui.TextPainter({
        text: textSpan,
        textDirection: ui.TextDirection.ltr,
      });

      textPainter.layout(canvas, { minWidth: 0.0, maxWidth: size.width / 2 });

      let width: number = textPainter.width;

      let height: number = textPainter.height;

      yAxisValuePainters.add(textPainter);

      maxYAxisValueWidth = Math.max(maxYAxisValueWidth, width);

      maxYAxisValueHeight = Math.max(maxYAxisValueHeight, height);

      stepValue += step;
    }

    let gapBetweenLabels: number = 1.0;

    let yAxisValuePadding: number = 10.0;

    let canvasHeight: number =
      size.height - maxYAxisValueHeight - yAxisValuePadding * 2;

    if (this.padding !== null) {
      tempHeight -= this.padding.top + this.padding.bottom;
    }

    if (this.canvasPadding !== null) {
      tempHeight -= this.canvasPadding.top + this.canvasPadding.bottom;
    } else {
      tempHeight -= 5.0 + 5.0;

      //  Default V padding for canvas
    }

    //  Reduce top and bottom margins

    tempHeight -= 5.0 * 2;

    let count: number = this.data.length;

    let barGap: number = 5.0;

    //  Reduce gaps

    tempHeight -= (count - 1) * barGap;

    let barHeight: number = tempHeight / count;

    if (barHeight > 60.0) {
      //  increase gaps instead of bar beight

      let extraBarHeight: number = barHeight - 60.0;

      barGap += extraBarHeight;

      barHeight = 60.0;
    }

    let labelPainters: Array<ui.TextPainter> = [];

    let maxLabelWidth: number = 0.0;

    //  Layout the X-Axis Labels

    for (let i: number = 0; i < this.data.length; i++) {
      // TextStyle textStyle = TextStyleExt.merge(defaultTextStyle, labelTextStyle??TextStyle(color: themeData.labelColor));

      let label: string = this.data[i].label;

      let style: ui.TextStyle = new ui.TextStyle({
        color: themeData.labelColor,
      });

      let textSpan: ui.TextSpan = new ui.TextSpan({
        text: label,
        style: style,
      });

      let textPainter: ui.TextPainter = new ui.TextPainter({
        text: textSpan,
        textDirection: ui.TextDirection.ltr,
      });

      textPainter.layout(canvas, { minWidth: 0.0, maxWidth: size.width / 2 });

      let width: number = textPainter.width;

      let height: number = textPainter.height;

      labelPainters.add(textPainter);

      maxLabelWidth = Math.max(maxLabelWidth, width);
    }

    let nthLabel: number = 1;

    //  By default we will try to show all labels if we have enough height

    let totalHeightOfLabels: number = 0.0;

    let shownLabels: number = 0;

    while (true) {
      totalHeightOfLabels = 0.0;

      shownLabels = 0;

      for (let i: number = 0; i < this.data.length; i += nthLabel) {
        let height: number = labelPainters[i].height;

        height +=
          (this.labelPadding?.top ?? 2.0) + (this.labelPadding?.bottom ?? 2.0);

        totalHeightOfLabels += height;

        shownLabels++;
      }

      //  If we have any mandatory gap between labels we add it here

      totalHeightOfLabels += (shownLabels - 1) * gapBetweenLabels;

      if (totalHeightOfLabels < size.height) {
        break;
      } else {
        nthLabel++;
      }
    }

    let extraGapPerLabel: number = size.height - totalHeightOfLabels;

    gapBetweenLabels += extraGapPerLabel / (shownLabels - 1).toDouble();

    let labelLeftPadding: number = this.labelPadding?.left ?? 10.0;

    let labelRightPadding: number = this.labelPadding?.right ?? 10.0;

    let chartLeftPadding: number = this.padding?.left ?? 5.0;

    let chartRightPadding: number = this.padding?.right ?? 5.0;

    //  Calculate max label size

    let canvasWidth: number =
      size.width -
      maxLabelWidth -
      (labelLeftPadding + labelRightPadding) -
      (chartLeftPadding + chartRightPadding);

    let barY: number = 0.0;

    let labelStart: number = chartLeftPadding + labelLeftPadding;

    let canvasStart: number = labelStart + maxLabelWidth + labelRightPadding;

    let canvasLeftPadding: number = this.canvasPadding?.left ?? 0.0;

    let canvasRightPadding: number = this.canvasPadding?.right ?? 0.0;

    let barStart: number = canvasStart + canvasLeftPadding;

    let lastYAxisValueWidth: number = yAxisValuePainters.isEmpty
      ? 0.0
      : yAxisValuePainters.last.width;

    let maxBarWidth: number =
      canvasWidth -
      canvasLeftPadding -
      canvasRightPadding -
      lastYAxisValueWidth / 2;

    let labelRightPosition: number =
      size.width - chartRightPadding - canvasWidth - labelRightPadding;

    let zeroPointWidth: number =
      ((0.0 - startValue) * maxBarWidth) / (totalRange - startValue);

    //  Draw Step lines

    let stepStart: number = minRange;

    let canvasTop: number =
      (this.padding?.top ?? 0.0) + (this.canvasPadding?.top ?? 0.0);

    let stepWidth: number = (step * maxBarWidth) / (totalRange - startValue);

    for (let i: number = 0; i < steps + 1; i++) {
      stepStart += step;

      let barWidth: number =
        (stepStart * maxBarWidth) / (totalRange - startValue);

      let stepPaint: ui.Paint = new ui.Paint();

      stepPaint.color = themeData.stepColor;

      stepPaint.style = ui.PaintingStyle.stroke;

      canvas.drawLine(
        ui.OffsetExt.getOffset({ dx: barStart + barWidth, dy: canvasTop + 5 }),
        ui.OffsetExt.getOffset({
          dx: barStart + barWidth,
          dy: canvasTop + canvasHeight + 5,
        }),
        stepPaint
      );
    }

    let nthYValue: number = 1;

    let shownSteps: number = 0;

    while (true) {
      let totalWithOfValueLabels: number = 0.0;

      shownSteps = 0;

      for (let i: number = 0; i < steps; i += nthYValue) {
        let width: number = yAxisValuePainters[i].width;

        //  TODO we need to use some other padding here. not label Padding

        width +=
          (this.labelPadding?.left ?? 2.0) + (this.labelPadding?.left ?? 2.0);

        totalWithOfValueLabels += width;

        shownSteps++;
      }

      //  If we have any mandatory gap between labels we add it here

      totalWithOfValueLabels += (shownSteps - 1) * gapBetweenLabels;

      if (totalWithOfValueLabels < canvasWidth) {
        break;
      } else {
        nthYValue++;
      }
    }

    //  Draw bar

    if (this.padding !== null) {
      barY += this.padding.top;
    }

    if (this.canvasPadding !== null) {
      barY += this.canvasPadding.top;
    } else {
      barY += 5.0;

      //  Default V padding for canvas
    }

    for (let i: number = 0; i < this.data.length; i++) {
      //  We might be using adaptive min...

      let value: number = this.data[i].doubleValue - startValue;

      //  bar width is proportional to maxBarWidth

      let barWidth: number = (value * maxBarWidth) / (totalRange - startValue);

      let barLeft: number = 0.0;

      if (value < 0.0) {
        barLeft = barStart + zeroPointWidth - barWidth;
      } else {
        barLeft = barStart + zeroPointWidth;
      }

      let rect: ui.Rect = ui.Rect.fromLTWH(barLeft, barY, barWidth, barHeight);

      let barPaint: ui.Paint = new ui.Paint();

      barPaint.color = themeData.dataColor(0);

      barPaint.style = ui.PaintingStyle.fill;

      canvas.drawRect(rect, barPaint);

      //  We only draw every nthLabel

      if (i % nthLabel === 0) {
        //  Draw label

        let painter: ui.TextPainter = labelPainters[i];

        let labelLeftPosition: number = labelRightPosition - painter.width;

        let labelTopPosition: number =
          barY + barHeight / 2 - painter.height / 2;

        painter.paint(
          canvas,
          ui.OffsetExt.getOffset({
            dx: labelLeftPosition,
            dy: labelTopPosition,
          })
        );
      }

      barY += barHeight;

      barY += barGap;
    }

    let stepPoint: number = barStart;

    let yAxisStart: number = canvasHeight + 10.0;

    for (let i: number = 0; i < steps + 2; i++) {
      if (i % nthYValue === 0) {
        //  Draw label

        let painter: ui.TextPainter = yAxisValuePainters[i];

        let width: number = painter.width;

        let labelLeftPosition: number = stepPoint - width / 2;

        painter.paint(
          canvas,
          ui.OffsetExt.getOffset({ dx: labelLeftPosition, dy: yAxisStart })
        );
      }

      stepPoint += stepWidth;
    }
  };
  public onLayoutChanged = (bounds: ui.Rect, globalPos: ui.Offset): void => {
    // doRepaint();
  };
}
export default function Bar2DChart(props: Bar2DChartProps) {
  return React.createElement(
    _Bar2DChartState,
    { ..._Bar2DChartState.defaultProps, ...props },
    ListWrapper.fromInput<ChartData>(props.data, "data")
  );
}
